// @ts-nocheck
import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useStyles from "../DaikinConsistency.style";
import ConsistencyLineView from "./ConsistencyLineView";

// normalizeVirtualLine

const normalizeVirtualLine = (line) => {
  const norm = (e) => ({
    ...e,
    systemRef: e.system ? String(e.system) : null,
    issues: e.issues ?? [],
    createdAt: e.createdAt ?? null,
  });
  return {
    ...line,
    summary: line.summary ?? { totalIssues: 0, highPriority: 0 },
    system: Object.fromEntries(Object.entries(line.system ?? {}).map(([k, s]) => [k, norm(s)])),
    control: Object.fromEntries(Object.entries(line.control ?? {}).map(([k, u]) => [k, norm(u)])),
    service: Object.fromEntries(Object.entries(line.service ?? {}).map(([k, u]) => [k, norm(u)])),
    outdoor: Object.fromEntries(Object.entries(line.outdoor ?? {}).map(([k, u]) => [k, norm(u)])),
  };
};

// VirtualSnapshotView

const VirtualSnapshotView = ({ result, selectedLine, onLineChange }) => {
  const classes = useStyles();
  const [grouping, setGrouping] = useState("system");

  const lineIds = Object.keys(result.lines);
  if (lineIds.length === 0) {
    return <Alert severity="info">No Daikin lines found on this device.</Alert>;
  }

  const currentLineId = lineIds[selectedLine];
  const currentLine = currentLineId ? result.lines[currentLineId] : null;

  const brandMismatchCount = currentLine
    ? [
        ...Object.values(currentLine.control ?? {}),
        ...Object.values(currentLine.service ?? {}),
        ...Object.values(currentLine.outdoor ?? {}),
      ].filter((u) => u.brandMismatch && !u.crossLine).length
    : 0;

  const normalizedLine = currentLine ? normalizeVirtualLine(currentLine) : null;

  return (
    <>
      {result.deviceRegistered === false && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          No Daikin lines in DB yet, but the device is reporting Daikin on{" "}
          {Object.entries(result.lines)
            .filter(([, l]) => l.isNewLine)
            .map(([id, l]) => `Line ${id} (new line, would be created as ${l.brand ?? "DK"})`)
            .join(", ")}
          . A device refresh would promote these lines to Daikin — virtual checks reflect that simulated state.
        </Alert>
      )}
      <Tabs value={selectedLine} onChange={(_, v) => onLineChange(v)} className={classes.tabsBar}>
        {lineIds.map((lineId) => {
          const line = result.lines[lineId];
          return (
            <Tab
              key={lineId}
              label={
                <Box className={classes.tabLabel}>
                  <span>
                    Line {lineId} ({line.brand})
                  </span>
                  {line.brandChanged && (
                    <Chip label="brand changed" color="warning" size="small" className={classes.chipMd} />
                  )}
                </Box>
              }
            />
          );
        })}
      </Tabs>
      {currentLine && normalizedLine && (
        <Box>
          {brandMismatchCount > 0 && (
            <Alert severity="warning" className={classes.lineStatusAlert}>
              {brandMismatchCount} unit{brandMismatchCount > 1 ? "s" : ""} on this line have a brand that doesn't match
              the line brand.
            </Alert>
          )}
          {currentLine.changes != null && (
            <Accordion className={classes.changeLogAccordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" fontWeight={600}>
                  Changes from virtual refresh&nbsp;
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({currentLine.changes.length})
                  </Typography>
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.changeLogDetails}>
                {currentLine.changes.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No changes detected.
                  </Typography>
                ) : (
                  currentLine.changes.map((change, i) => (
                    <Typography key={i} variant="caption" display="block" className={classes.changeLogEntry}>
                      • {change}
                    </Typography>
                  ))
                )}
              </AccordionDetails>
            </Accordion>
          )}
          <Box display="flex" alignItems="center" justifyContent="flex-end" mb={1.5}>
            <Box display="flex" gap={0.5}>
              <Button
                size="small"
                variant={grouping === "system" ? "contained" : "outlined"}
                onClick={() => setGrouping("system")}
              >
                By System
              </Button>
              <Button
                size="small"
                variant={grouping === "type" ? "contained" : "outlined"}
                onClick={() => setGrouping("type")}
              >
                By Type
              </Button>
            </Box>
          </Box>
          <ConsistencyLineView line={normalizedLine} grouping={grouping} showIssues={false} />
        </Box>
      )}
    </>
  );
};

export default VirtualSnapshotView;
